/**
 * useFormState Hook
 * Manages form state with support for nested fields
 * Reduces boilerplate in form-heavy components
 */

import { useState, useCallback } from 'react';

export const useFormState = (initialData = {}) => {
  const [formData, setFormData] = useState(initialData);

  /**
   * Handle simple field changes (text, email, checkbox, etc.)
   */
  const handleChange = useCallback((e) => {
    const { name, type, checked, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  /**
   * Handle nested field changes (e.g., address.city)
   */
  const handleNestedChange = useCallback((path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  /**
   * Handle nested object changes (e.g., mapAddress)
   */
  const handleNestedObjectChange = useCallback((parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value,
      },
    }));
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormData(initialData);
  }, [initialData]);

  /**
   * Update entire form data
   */
  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  /**
   * Get a nested value safely
   */
  const getNestedValue = useCallback((path, defaultValue = '') => {
    const keys = path.split('.');
    let value = formData;

    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return defaultValue;
    }

    return value ?? defaultValue;
  }, [formData]);

  return {
    formData,
    setFormData,
    handleChange,
    handleNestedChange,
    handleNestedObjectChange,
    resetForm,
    updateFormData,
    getNestedValue,
  };
};
